/**
 * Keeps the floating WhatsApp button off the buying controls.
 *
 * The button is `position: fixed` in the bottom-right corner. On a phone, the
 * product page puts the size and colour pickers in exactly that corner, so the
 * button sat on top of the controls a shopper has to tap. Screenshots at 390px
 * showed it covering the second size option.
 *
 * Rather than drop the button from product pages — the chat message there is
 * context-aware and names the product, which is the most useful version of it —
 * it retracts while the buying controls are on screen and comes back once they
 * scroll away. On the images, the details rows and the recommendations it is
 * still there.
 *
 * Narrow viewports only. Above 750px the controls sit in a right-hand column
 * and never reach the corner, so there is nothing to avoid.
 */

// Horizon renders the picker and buy buttons as custom elements; the class
// selectors are a fallback in case a section uses the plain markup.
const CONTROL_SELECTOR = [
  'variant-picker',
  'buy-buttons',
  'product-form-component',
  '.variant-picker',
  '.buy-buttons',
].join(', ');

const NARROW = '(max-width: 749px)';
const RETRACTED = 'whatsapp-button--retracted';

if (!customElements.get('whatsapp-fab')) {
  customElements.define(
    'whatsapp-fab',
    class WhatsappFab extends HTMLElement {
      connectedCallback() {
        this.link = this.querySelector('.whatsapp-button');
        if (!this.link) return;

        const controls = document.querySelectorAll(CONTROL_SELECTOR);
        if (!controls.length) return;

        // Seed the map before observing. The observer fires synchronously on
        // the first frame, and reading an unbuilt map there would throw.
        this.visible = new Map();

        this.mq = window.matchMedia(NARROW);
        this.onChange = () => this.apply();

        this.observer = new IntersectionObserver(
          (entries) => {
            for (const entry of entries) this.visible.set(entry.target, entry.isIntersecting);
            this.apply();
          },
          // A sliver of a control counts as present. Anything stricter let the
          // button reappear over a half-scrolled picker.
          { threshold: 0 }
        );

        for (const control of controls) this.observer.observe(control);

        // matchMedia rather than a resize listener: rotating a phone is the
        // only realistic way to cross this breakpoint mid-session.
        this.mq.addEventListener('change', this.onChange);
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.mq?.removeEventListener('change', this.onChange);
      }

      apply() {
        const overlapping = this.mq.matches && [...this.visible.values()].some(Boolean);
        this.link.classList.toggle(RETRACTED, overlapping);
        // Keep it out of the tab order while it is off screen, so keyboard
        // focus never lands on a button nobody can see.
        this.link.toggleAttribute('inert', overlapping);
      }
    }
  );
}
